import { moveSlidingToggle } from './sliding-toggle.js';
/* Present the original CreditsYard widget inline. CreditsYard owns all account
   data, offers, settings and checkout actions. No cloned controls or ledger. */
const page = document.querySelector('[data-store-credit-page]');
if (page) {
  const slot = page.querySelector('[data-store-credit-slot]');
  const preview = new URLSearchParams(location.search).get('creditsyard-preview') === '1';
  const authenticated = page.dataset.authenticated === 'true';
  let root;
  let initialized = false;
  let timeout;
  let startupObserver;
  let providerScript;
  let providerResponseStarted = false;
  let tabObserver;
  let heightBeforeTab;
  let panelAnimation;
  const show = (name, visible) => {
    const element = page.querySelector(`[data-store-credit-${name}]`);
    if (element) element.hidden = !visible;
  };
  const finishLoading = () => {
    clearTimeout(timeout);
    startupObserver?.disconnect();
    show('loading', false);
    show('error', false);
    slot.removeAttribute('aria-busy');
  };
  // Check a fresh Shopify bag before handing back to the unchanged provider action.
  // A successful check never calculates entitlement, changes credit, or creates an order.
  const checkoutNotice = page.querySelector('[data-credit-checkout-notice]');
  const providerClicks = new WeakSet();
  let checkingCart = false;
  let checkoutButton;
  const setCheckoutLoading = (button, loading) => {
    if (!button) return;
    button.dispatchEvent(new CustomEvent('sigma:button-loading', {
      bubbles: true, detail: { loading },
    }));
    checkoutButton = loading ? button : null;
  };
  const showCheckoutNotice = (empty) => {
    if (!checkoutNotice) return;
    checkoutNotice.querySelector('[data-credit-checkout-title]').textContent = empty
      ? 'Your bag is empty' : 'We couldn’t check your bag';
    checkoutNotice.querySelector('[data-credit-checkout-message]').textContent = empty
      ? 'Add a coffee from The Library to your bag, then return here to use your credit.'
      : 'Your credit hasn’t been used. Check your connection, then choose Use credit at checkout to try again.';
    checkoutNotice.querySelector('[data-credit-checkout-browse]').hidden = !empty;
    checkoutNotice.hidden = false;
    checkoutNotice.focus();
  };
  document.addEventListener('sigma:mystery-credit-blocked', event => {
    setCheckoutLoading(checkoutButton, false);
    if (!checkoutNotice) return;
    const blocked = event.detail?.reason === 'private-mystery-hold';
    checkoutNotice.querySelector('[data-credit-checkout-title]').textContent = blocked
      ? 'Store credit is unavailable for this bag' : 'We couldn’t check credit eligibility';
    checkoutNotice.querySelector('[data-credit-checkout-message]').textContent = blocked
      ? 'Library Card credit can’t be used while your bag contains a mystery box. You can still use the usual checkout, or remove the mystery box to use your credit.'
      : 'Your credit hasn’t been used. Please try again, or use the usual bag checkout.';
    checkoutNotice.querySelector('[data-credit-checkout-browse]').hidden = true;
    checkoutNotice.hidden = false;
    checkoutNotice.focus();
  });
  page.addEventListener('click', async event => {
    const button = event.target.closest('.creditsyard-apply-credit-button');
    if (!button || providerClicks.has(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingCart || checkoutButton || button.disabled) return;
    checkingCart = true;
    setCheckoutLoading(button, true);
    if (checkoutNotice) checkoutNotice.hidden = true;
    const controller = new AbortController();
    const abort = setTimeout(() => controller.abort(), 8000);
    let hasItems = false;
    try {
      const response = await fetch(page.dataset.cartUrl, {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error('Invalid cart');
      hasItems = cart.item_count > 0;
      if (!hasItems) showCheckoutNotice(true);
    } catch {
      showCheckoutNotice(false);
    } finally {
      clearTimeout(abort);
      checkingCart = false;
      if (!hasItems) setCheckoutLoading(button, false);
    }
    if (hasItems && button.isConnected) {
      providerClicks.add(button);
      try { button.click(); } catch {
        setCheckoutLoading(button, false);
        showCheckoutNotice(false);
      } finally { providerClicks.delete(button); }
    }
  }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted) setCheckoutLoading(checkoutButton, false);
  });
  // Tab change: capture the visible card height, switch provider content, then
  // settle to its intrinsic height. A new selection interrupts from the current size.
  const animateCardHeight = () => {
    const card = root.querySelector('.creditsyard-widget-wrapper');
    if (!card || heightBeforeTab === undefined) return;
    const from = heightBeforeTab;
    heightBeforeTab = undefined;
    panelAnimation?.cancel();
    const to = card.getBoundingClientRect().height;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(to - from) < 1) return;
    card.dataset.creditResizing = '';
    const animation = card.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)',
    });
    panelAnimation = animation;
    animation.finished.catch(() => {}).finally(() => {
      if (panelAnimation === animation) {
        card.removeAttribute('data-credit-resizing');
        panelAnimation = null;
      }
    });
  };
  const syncTabs = () => {
    if (!root) return;
    const content = root.querySelector('.creditsyard-content-wrapper');
    const active = content?.dataset.activeTab;
    moveSlidingToggle(root.querySelector('.storefront-credit-indicator'), active === 'cashback' ? 1 : 0);
    root.querySelectorAll('.creditsyard-tab-item').forEach((tab, index) => {
      const selected = tab.dataset.tab === active;
      tab.id = `library-card-tab-${index}`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = Array.from(root.querySelectorAll('[data-tab-content]')).find(item => item.dataset.tabContent === tab.dataset.tab);
      if (panel) {
        panel.id = `library-card-panel-${index}`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        tab.setAttribute('aria-controls', panel.id);
        panel.hidden = !selected;
      }
    });
    animateCardHeight();
  };
  const styleTransactions = () => {
    root.querySelectorAll('.transaction-reason').forEach(reasonElement => {
      const row = reasonElement.closest('li');
      if (!row) return;
      // Provider preview omits type metadata. Classify only explicit labels;
      // unknown adjustments retain their original reason and neutral treatment.
      const reason = row.querySelector('.transaction-reason')?.textContent || '';
      const type = `${row.dataset.transactionType || ''} ${reason}`.toLowerCase();
      const value = row.querySelector('.transaction-value');
      const kind = /refund/.test(type) ? 'refund'
        : /top[\s_-]*up/.test(type) ? 'topup'
        : /cash[\s_-]*back/.test(type) ? 'cashback'
        : value?.classList.contains('credit-added') || /credit[\s_-]*added/.test(type) ? 'added'
        : value?.classList.contains('credit-deducted') || /credit[\s_-]*deducted/.test(type) ? 'deducted'
        : /\bused\b|credit[\s_-]*used|spent|purchase/.test(type) ? 'deducted' : 'other';
      const previousKind = row.dataset.creditKind;
      row.dataset.creditKind = kind;
      const status = row.querySelector('.transaction-status');
      if (!status) return;
      let providerStatus = status.querySelector('[data-credit-provider-status]');
      if (previousKind === kind && providerStatus && status.querySelector('[data-credit-state-icon]')) return;
      if (!providerStatus) {
        providerStatus = document.createElement('span');
        providerStatus.dataset.creditProviderStatus = '';
        while (status.firstChild) providerStatus.append(status.firstChild);
        status.append(providerStatus);
      }
      status.querySelector('[data-credit-state-icon]')?.remove();
      providerStatus.classList.add('coffee-product__visually-hidden');
      const icon = page.querySelector(`[data-credit-icon="${kind}"]`);
      if (icon) {
        const replacement = icon.content.cloneNode(true);
        replacement.firstElementChild?.setAttribute('data-credit-state-icon', '');
        status.prepend(replacement);
      }
    });
  };
  const mount = () => {
    const candidate = document.getElementById('creditsyard-root');
    if (!candidate?.querySelector('.creditsyard-customer-balance')) return false;
    if (candidate !== root) {
      tabObserver?.disconnect();
      root = candidate;
      slot.append(root);
      root.dataset.inlineCreditPage = 'true';
      const balanceTitle = root.querySelector('.customer-balance-title');
      if (balanceTitle) balanceTitle.textContent = 'Your current balance';
      const balance = root.querySelector('.creditsyard-customer-balance');
      if (balance) {
        const help = document.createElement('p');
        help.className = 'store-credit-page__balance-help';
        help.textContent = 'Available to spend on coffee in The Library.';
        balance.append(help);
      }
      const topupTab = root.querySelector('.creditsyard-tab-item[data-tab="cashback"]');
      if (topupTab) topupTab.textContent = 'Monthly top-up';
      const tabs = root.querySelector('.creditsyard-tab-items');
      if (tabs && !tabs.querySelector('.storefront-credit-indicator')) {
        const indicator = document.createElement('span');
        indicator.className = 'storefront-credit-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        tabs.prepend(indicator);
        tabs.classList.add('storefront-credit-tabs');
      }
      tabs?.setAttribute('role', 'tablist');
      tabs?.setAttribute('aria-label', 'Library Card');
      tabs?.addEventListener('click', event => {
        if (event.target.closest('.creditsyard-tab-item')) {
          heightBeforeTab = root.querySelector('.creditsyard-widget-wrapper')?.getBoundingClientRect().height;
        }
      }, true);
      tabs?.addEventListener('keydown', event => {
        const tab = event.target.closest('.creditsyard-tab-item');
        if (!tab) return;
        const items = Array.from(tabs.querySelectorAll('.creditsyard-tab-item'));
        const index = items.indexOf(tab);
        let next;
        if (event.key === 'ArrowRight') next = items[(index + 1) % items.length];
        if (event.key === 'ArrowLeft') next = items[(index - 1 + items.length) % items.length];
        if (event.key === 'Home') next = items[0];
        if (event.key === 'End') next = items.at(-1);
        if (event.key === 'Enter' || event.key === ' ') next = tab;
        if (next) { event.preventDefault(); next.click(); next.focus(); }
      });
      const content = root.querySelector('.creditsyard-content-wrapper');
      if (content) {
        tabObserver = new MutationObserver(mutations => {
          syncTabs();
          if (mutations.some(mutation => mutation.type === 'childList')) styleTransactions();
        });
        tabObserver.observe(content, { attributes: true, attributeFilter: ['data-active-tab'], childList: true, subtree: true });
      }
      const error = root.querySelector('#creditsyard-error-message');
      error?.setAttribute('role', 'alert');
      if (error) new MutationObserver(() => {
        if (checkoutButton && error.querySelector('.creditsyard-error-message-text')?.textContent.trim()) {
          setCheckoutLoading(checkoutButton, false);
        }
      }).observe(error, { childList: true, subtree: true, characterData: true });
      const dismiss = error?.querySelector('[onclick]');
      if (dismiss && dismiss.tagName !== 'BUTTON') {
        dismiss.setAttribute('role', 'button');
        dismiss.setAttribute('aria-label', 'Dismiss credit message');
        dismiss.tabIndex = 0;
        dismiss.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); dismiss.click(); }
        });
      }
      // Reuse the established button surface without changing provider handlers.
      root.querySelectorAll('.creditsyard-apply-credit-button').forEach(button => {
        button.classList.add('button', 'button--primary');
        const label = button.querySelector('.button-content');
        if (label) {
          label.classList.add('button__content');
          label.textContent = 'Use credit at checkout';
          const icon = page.querySelector('[data-credit-icon="used"]');
          if (icon) label.prepend(icon.content.cloneNode(true));
        }
        button.setAttribute('aria-label', 'Use credit at checkout');
        button.setAttribute('data-button-loading-text', 'Loading...');
        // Observe provider state without replacing its checkout handler.
        let providerWasLoading = false;
        new MutationObserver(() => {
          const loading = button.classList.contains('button-loading');
          if (loading) setCheckoutLoading(button, true);
          else if (providerWasLoading) setCheckoutLoading(button, false);
          providerWasLoading = loading;
        }).observe(button, { attributes: true, attributeFilter: ['class'] });
        const help = document.createElement('p');
        help.className = 'store-credit-page__checkout-help';
        help.id = 'LibraryCreditCheckoutHelp';
        help.textContent = 'For eligible coffee in your bag.';
        button.setAttribute('aria-describedby', help.id);
        button.after(help);
      });
      root.querySelectorAll('.cashback-actions a').forEach(link => link.classList.add('button', 'button--secondary'));
      root.querySelectorAll('.cashback-actions li').forEach(offer => {
        const image = offer.querySelector('.cashback-action-icon svg, .cashback-action-icon img');
        const icon = page.querySelector('[data-credit-icon="topup"]');
        if (/top[\s_-]*up/i.test(offer.textContent)) {
          const title = offer.querySelector('.cashback-action-title');
          const description = offer.querySelector('.cashback-action-description');
          const link = offer.querySelector('a');
          if (title) title.textContent = 'Monthly credit subscription';
          if (description) description.textContent = 'Choose a monthly amount. Your payment repeats each month and adds credit to your Library Card.';
          if (link) link.textContent = 'View monthly plans';
        }
        if (image && icon && /monthly credit subscription/i.test(offer.textContent)) {
          const holder = document.createElement('span');
          holder.dataset.creditOfferIcon = '';
          holder.append(icon.content.cloneNode(true));
          image.replaceWith(holder);
        }
      });
    }
    syncTabs();
    styleTransactions();
    finishLoading();
    show('intro', false);
    const launcher = root.querySelector('.creditsyard-tab-block');
    const knownZero = launcher?.dataset.hasCredit === '0';
    show('empty', knownZero);
    page.dataset.creditState = knownZero ? 'empty' : 'ready';
    return true;
  };
  document.addEventListener('creditsyard-init', event => {
    initialized = true;
    if (mount()) {
      const balance = event.detail?.balance;
      if (balance !== null && balance !== undefined && balance !== '' && Number.isFinite(Number(balance))) {
        show('empty', Number(balance) === 0);
        page.dataset.creditState = Number(balance) === 0 ? 'empty' : 'ready';
      }
    } else {
      finishLoading();
      // An incompatible provider response must not look like a new account.
      const incompatible = Boolean(document.getElementById('creditsyard-root')) || Number(event.detail?.balance) > 0;
      show('intro', !incompatible);
      show('error', incompatible);
      page.dataset.creditState = incompatible ? 'error' : 'new';
    }
  });
  // Provider inserts the root asynchronously. Observe only root-level additions,
  // then its own init event handles future refreshes.
  const observer = new MutationObserver(() => { if (mount()) observer.disconnect(); });
  observer.observe(document.body, { childList: true });
  const loadingStatus = page.querySelector('.page-loading-skeleton__status');
  const showLoadFailure = (startup) => {
    if (mount() || initialized) return;
    show('loading', false);
    show('error', true);
    slot.removeAttribute('aria-busy');
    page.dataset.creditState = 'error';
    const message = page.querySelector('[data-store-credit-error] p');
    if (message) message.textContent = startup
      ? 'The credit service is taking longer to connect. Please try again. Your store credit is unchanged.'
      : 'We couldn’t load your balance. Please try again. Your store credit is unchanged.';
  };
  const beginProviderResponse = () => {
    if (providerResponseStarted || initialized || root) return;
    providerResponseStarted = true;
    startupObserver?.disconnect();
    clearTimeout(timeout);
    show('error', false);
    show('loading', true);
    slot.setAttribute('aria-busy', 'true');
    page.dataset.creditState = 'loading';
    page.dataset.creditLoadingPhase = 'balance';
    if (loadingStatus) loadingStatus.textContent = 'Loading your Library Card…';
    timeout = setTimeout(() => showLoadFailure(false), 15000);
  };
  const providerScriptFailed = () => {
    clearTimeout(timeout);
    showLoadFailure(true);
  };
  const watchProviderStartup = () => {
    // Shopify inserts its app scripts after window.load. Do not start the
    // balance deadline while that script has not even been downloaded yet.
    const script = document.querySelector('script[src*="/js/integrations/script.js"]');
    if (script && script !== providerScript) {
      providerScript = script;
      script.addEventListener('load', beginProviderResponse, { once: true });
      script.addEventListener('error', providerScriptFailed, { once: true });
    }
    // Covers a cached provider that finished before this module attached.
    if (typeof window.StoreCredit?.initStoreCredit === 'function') beginProviderResponse();
  };
  if (!mount() && (authenticated || preview)) {
    show('intro', false);
    show('loading', true);
    slot.setAttribute('aria-busy', 'true');
    page.dataset.creditState = 'loading';
    page.dataset.creditLoadingPhase = 'connecting';
    if (loadingStatus) loadingStatus.textContent = 'Connecting to your Library Card…';
    // Bound startup separately; a blocked page resource must not leave an
    // endless skeleton. Keep watching for late success after either deadline.
    timeout = setTimeout(() => showLoadFailure(true), 45000);
    startupObserver = new MutationObserver(watchProviderStartup);
    startupObserver.observe(document.documentElement, { childList: true, subtree: true });
    watchProviderStartup();
  } else if (!root) {
    show('loading', false);
    show('intro', true);
    page.dataset.creditState = 'signed-out';
  }
  page.querySelector('[data-store-credit-retry]')?.addEventListener('click', () => location.reload());
  window.addEventListener('pagehide', () => { observer.disconnect(); startupObserver?.disconnect(); providerScript?.removeEventListener('load', beginProviderResponse); providerScript?.removeEventListener('error', providerScriptFailed); tabObserver?.disconnect(); panelAnimation?.cancel(); clearTimeout(timeout); }, { once: true });
}
